import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, parseISO, startOfWeek, endOfWeek, subMonths, addMonths } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Download, X, TrendingUp, Users, Percent } from 'lucide-react';
import { VentureCard } from '@/components/ui/venture-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useEODContext } from '@/components/eod/EODProvider';
import { EODSubmission, LEAD_QUALITY_OPTIONS } from '@/types/eod';

export default function EODReports() {
  const { submissions, getSubmissionByDate } = useEODContext();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedSubmission, setSelectedSubmission] = useState<EODSubmission | null>(null);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart);
    const calEnd = endOfWeek(monthEnd);
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentMonth]);

  const weeklyStats = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now);
    const weekEnd = endOfWeek(now);
    
    const weekSubmissions = submissions.filter(s => {
      const date = parseISO(s.date);
      return date >= weekStart && date <= weekEnd;
    });

    const totalDemos = weekSubmissions.reduce((sum, s) => sum + s.demos_booked, 0);
    const totalShowed = weekSubmissions.reduce((sum, s) => sum + s.demos_showed, 0);
    const totalClosed = weekSubmissions.reduce((sum, s) => sum + s.demos_closed, 0);
    const avgShowRate = totalDemos > 0 ? Math.round((totalShowed / totalDemos) * 100) : 0;

    return {
      totalDemos,
      totalClosed,
      avgShowRate,
      submissionCount: weekSubmissions.length
    };
  }, [submissions]);

  const handleExportCSV = () => {
    const headers = ['Date', 'Demos Booked', 'Demos Showed', 'Demos Closed', 'Revenue Closed', 'Show Rate', 'Lead Quality', 'Issues', 'Notes', 'Tomorrow Focus', 'Submitted At'];
    const rows = submissions.map(s => [
      s.date,
      s.demos_booked.toString(),
      s.demos_showed.toString(),
      s.demos_closed.toString(),
      s.revenue_closed?.toString() || '',
      `${s.show_rate}%`,
      s.lead_quality,
      `"${s.issues.replace(/"/g, '""')}"`,
      `"${s.notes.replace(/"/g, '""')}"`,
      `"${s.tomorrow_focus.replace(/"/g, '""')}"`,
      s.submitted_at
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `eod-reports-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getQualityConfig = (quality: string) => {
    return LEAD_QUALITY_OPTIONS.find(o => o.value === quality) || LEAD_QUALITY_OPTIONS[0];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">EOD Reports</h1>
          <p className="text-muted-foreground">View and track your daily submissions</p>
        </div>
        <Button variant="secondary" onClick={handleExportCSV}>
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Weekly Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <VentureCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Demos Booked</p>
              <p className="text-xl font-bold text-foreground">{weeklyStats.totalDemos}</p>
            </div>
          </div>
        </VentureCard>
        <VentureCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Demos Closed</p>
              <p className="text-xl font-bold text-foreground">{weeklyStats.totalClosed}</p>
            </div>
          </div>
        </VentureCard>
        <VentureCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <Percent className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Show Rate</p>
              <p className="text-xl font-bold text-foreground">{weeklyStats.avgShowRate}%</p>
            </div>
          </div>
        </VentureCard>
        <VentureCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-info/10">
              <Calendar className="w-5 h-5 text-info" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Days Submitted</p>
              <p className="text-xl font-bold text-foreground">{weeklyStats.submissionCount}/7</p>
            </div>
          </div>
        </VentureCard>
      </div>

      {/* Calendar View */}
      <VentureCard className="p-6">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Calendar className="w-5 h-5 text-secondary" />
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentMonth(m => subMonths(m, 1))}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentMonth(new Date())}
            >
              Today
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentMonth(m => addMonths(m, 1))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Day headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground">
              {day}
            </div>
          ))}

          {/* Days */}
          {calendarDays.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const submission = getSubmissionByDate(dateStr);
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isTodayDate = isToday(day);
            const isPast = day < new Date() && !isTodayDate;

            return (
              <motion.button
                key={dateStr}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => submission && setSelectedSubmission(submission)}
                disabled={!submission}
                className={`
                  relative p-2 min-h-[60px] rounded-lg border transition-all text-left
                  ${!isCurrentMonth ? 'opacity-30' : ''}
                  ${isTodayDate ? 'border-secondary ring-1 ring-secondary/30' : 'border-border'}
                  ${submission ? 'cursor-pointer hover:border-success bg-success/5' : 'cursor-default'}
                  ${isPast && !submission ? 'bg-destructive/5 border-destructive/20' : ''}
                `}
              >
                <span className={`text-xs ${isTodayDate ? 'text-secondary font-bold' : 'text-muted-foreground'}`}>
                  {format(day, 'd')}
                </span>
                {submission && (
                  <div className="mt-1">
                    <div className="w-2 h-2 rounded-full bg-success absolute top-2 right-2" />
                    <p className="text-xs text-success font-medium">{submission.demos_booked}d</p>
                  </div>
                )}
                {isPast && !submission && isCurrentMonth && (
                  <div className="w-2 h-2 rounded-full bg-destructive absolute top-2 right-2" />
                )}
              </motion.button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-success" />
            <span>Submitted</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-destructive" />
            <span>Missed</span>
          </div>
        </div>
      </VentureCard>

      {/* Recent Submissions List */}
      <VentureCard className="p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Recent Submissions</h2>
        <div className="space-y-3">
          {submissions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No submissions yet</p>
          ) : (
            submissions
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .slice(0, 10)
              .map(submission => {
                const qualityConfig = getQualityConfig(submission.lead_quality);
                return (
                  <motion.button
                    key={submission.id}
                    whileHover={{ x: 4 }}
                    onClick={() => setSelectedSubmission(submission)}
                    className="w-full flex items-center justify-between p-4 rounded-lg border border-border bg-surface hover:border-secondary/50 transition-all text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-lg font-bold text-foreground">{format(parseISO(submission.date), 'd')}</p>
                        <p className="text-xs text-muted-foreground">{format(parseISO(submission.date), 'MMM')}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {submission.demos_booked} booked • {submission.demos_closed} closed
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-2">
                          <span>{submission.show_rate}% show rate</span>
                          <span>{qualityConfig.emoji} {qualityConfig.label.split(' ')[0]}</span>
                          {submission.submitted_late && (
                            <Badge variant="outline" className="text-warning border-warning/30">Late</Badge>
                          )}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </motion.button>
                );
              })
          )}
        </div>
      </VentureCard>

      {/* Detail Modal */}
      {selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
        />
      )}
    </div>
  );
}

function SubmissionDetailModal({ submission, onClose }: { submission: EODSubmission; onClose: () => void }) {
  const qualityConfig = LEAD_QUALITY_OPTIONS.find(o => o.value === submission.lead_quality) || LEAD_QUALITY_OPTIONS[0];
  const closeRate = submission.demos_showed > 0 
    ? Math.round((submission.demos_closed / submission.demos_showed) * 100) 
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-lg m-4 p-6 rounded-lg border border-border bg-card shadow-xl"
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>

        <h2 className="text-xl font-bold text-foreground mb-1">
          {format(parseISO(submission.date), 'EEEE, MMMM d, yyyy')}
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          Submitted {format(parseISO(submission.submitted_at), 'h:mm a')}
          {submission.submitted_late && ' (Late)'}
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-surface border border-border">
              <p className="text-xs text-muted-foreground">Demos Booked</p>
              <p className="text-lg font-bold text-foreground">{submission.demos_booked}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface border border-border">
              <p className="text-xs text-muted-foreground">Demos Showed</p>
              <p className="text-lg font-bold text-foreground">{submission.demos_showed}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface border border-border">
              <p className="text-xs text-muted-foreground">Demos Closed</p>
              <p className="text-lg font-bold text-green-400">{submission.demos_closed}</p>
            </div>
            <div className="p-3 rounded-lg bg-surface border border-border">
              <p className="text-xs text-muted-foreground">Show Rate</p>
              <p className={`text-lg font-bold ${submission.show_rate >= 70 ? 'text-success' : submission.show_rate >= 50 ? 'text-warning' : 'text-destructive'}`}>
                {submission.show_rate}%
              </p>
            </div>
          </div>

          {submission.revenue_closed && (
            <div className="p-3 rounded-lg bg-surface border border-border">
              <p className="text-xs text-muted-foreground mb-1">Revenue Closed</p>
              <p className="text-lg font-bold text-green-400">${submission.revenue_closed.toLocaleString()}</p>
            </div>
          )}

          <div className="p-3 rounded-lg bg-surface border border-border">
            <p className="text-xs text-muted-foreground mb-1">Lead Quality</p>
            <p className="text-foreground">{qualityConfig.emoji} {qualityConfig.label}</p>
          </div>

          {submission.lead_quality_issues && (
            <div className="p-3 rounded-lg bg-surface border border-border">
              <p className="text-xs text-muted-foreground mb-1">Quality Issues</p>
              <p className="text-foreground text-sm">{submission.lead_quality_issues}</p>
            </div>
          )}

          {submission.issues && (
            <div className="p-3 rounded-lg bg-surface border border-border">
              <p className="text-xs text-muted-foreground mb-1">Issues</p>
              <p className="text-foreground text-sm">{submission.issues}</p>
            </div>
          )}

          {submission.notes && (
            <div className="p-3 rounded-lg bg-surface border border-border">
              <p className="text-xs text-muted-foreground mb-1">Notes</p>
              <p className="text-foreground text-sm">{submission.notes}</p>
            </div>
          )}

          {submission.tomorrow_focus && (
            <div className="p-3 rounded-lg bg-surface border border-border">
              <p className="text-xs text-muted-foreground mb-1">Tomorrow's Focus</p>
              <p className="text-foreground text-sm">{submission.tomorrow_focus}</p>
            </div>
          )}
        </div>

        <Button variant="ghost-border" className="w-full mt-6" onClick={onClose}>
          Close
        </Button>
      </motion.div>
    </div>
  );
}
